// web/src/features/caf/CafListDev.jsx
import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { listCafs } from '../../services/firestore/cafService'
import { listCommunitiesFS } from '../../services/firestore/communityService'
import { getSession } from '@/lib/auth'

// near the top of each file (optional helper)
const HEADER_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 0,
  marginBottom: 12,
}
const STATUS_OPTIONS = [
  'All',
  'draft',
  'submitted',
  'in_review',
  'changes_requested',
  'approved',
  'complete',
  'void',
]
const STAGE_OPTIONS = [
  'All',
  'Manager',
  'Third-Party Owner (if required)',
  'Regional Manager',
  'Assistant Director of Facilities',
  'Director of Facilities',
  'VP / Facilities',
  'VP / Operations',
]

/* ----- CSV helpers ----- */
function csvEscape(v) {
  if (v == null) return ''
  const s = String(v)
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

function timestampToIso(value) {
  if (!value) return ''
  if (value?.seconds) return new Date(value.seconds * 1000).toISOString()
  if (typeof value?.toDate === 'function') return value.toDate().toISOString()
  return value || ''
}

function getCurrentStatusForReport(caf) {
  const status = String(caf?.status || '').trim()
  if (
    caf?.isAmendment !== true &&
    caf?.completedViaAmendmentId &&
    status.toLowerCase() === 'approved'
  ) {
    const amendmentLabel = caf.completedViaAmendmentNumber
      ? `Amendment ${caf.completedViaAmendmentNumber}`
      : caf.completedViaAmendmentId
    return `complete via ${amendmentLabel}`
  }
  return status
}

function getRecommendedProposal(caf) {
  const proposals = Array.isArray(caf?.proposals) ? caf.proposals : []
  return proposals.find(p => p?.recommended === true) || null
}

function getNumericOrBlank(value) {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(value)
  return Number.isNaN(n) ? '' : n
}

// Budget / actual / variance for export (raw numbers)
function getAmountsForExport(caf) {
  const rawBudget = caf.projectBudget
  const rawActual = caf.projectActual

  const b = Number(rawBudget)
  const a = Number(rawActual)

  const hasBudget =
    rawBudget !== null &&
    rawBudget !== undefined &&
    !Number.isNaN(b)
  const hasActual =
    rawActual !== null &&
    rawActual !== undefined &&
    !Number.isNaN(a)

  if (!hasBudget || !hasActual) {
    return { budget: '', actual: '', variance: '' }
  }

  const diff = a - b
  return { budget: b, actual: a, variance: diff }
}

function exportCafListCsv(rows, getCommunityName) {
  const header = [
    'CAF Number',
    'CAF ID',
    'Community',
    'Community Code',
    'Title',
    'Current Status',
    'Raw Status',
    'Stage',
    'Facility Type',
    'CAF Type',
    'Parent CAF',
    'Root CAF',
    'Amendment #',
    'Active Amendment',
    'Completed Via Amendment',
    'Created At',
    'Updated At',
    'Created By',
    'Created By Email',
    'Budgeted Amount',
    'Not Budgeted',
    'Original Approved Amount',
    'Amendment Change Amount',
    'Revised Project Total',
    'Current Authorized Total',
    'Project Budget',
    'Project Actual',
    'Variance (Actual - Budget)',
    'Project Completion Date',
    'Final Approver',
    'Approval Chain',
    'Approval History Count',
    'Proposal Count',
    'Recommended Vendor',
    'Recommended Proposal Amount',
    'File Count',
    'Tags',
    'Void Reason',
    'Amendment Reason',
    'Record URL',
  ]
  const lines = [header.join(',')]

  for (const x of rows) {
    const { budget, actual, variance } = getAmountsForExport(x)
    const approvalSequence = Array.isArray(x.approvals?.sequence)
      ? x.approvals.sequence
      : []
    const approvalHistory = Array.isArray(x.approvals?.history)
      ? x.approvals.history
      : []
    const recommendedProposal = getRecommendedProposal(x)
    const recommendedAmount = getNumericOrBlank(recommendedProposal?.price)
    const currentAuthorizedTotal = getNumericOrBlank(
      x.currentAuthorizedTotal ?? x.revisedTotalAmount ?? x.contractPrice,
    )

    const line = [
      x.number || '',
      x.id || '',
      getCommunityName(x.communityCode),
      x.communityCode || '',
      x.title || '',
      getCurrentStatusForReport(x),
      x.status || '',
      x.stage || '',
      x.facilityType || '',
      x.isAmendment === true ? 'Amendment' : 'Original',
      x.parentCafNumber || x.parentCafId || '',
      x.rootCafNumber || x.rootCafId || '',
      x.amendmentNumber ?? '',
      x.activeAmendmentId || '',
      x.completedViaAmendmentId || '',
      timestampToIso(x.createdAt),
      timestampToIso(x.updatedAt),
      x.createdByName || '',
      x.createdByEmail || '',
      getNumericOrBlank(x.contractPrice),
      x.notBudgeted === true ? 'Yes' : 'No',
      getNumericOrBlank(x.originalApprovedAmount),
      getNumericOrBlank(x.amendmentDeltaAmount),
      getNumericOrBlank(x.revisedTotalAmount),
      currentAuthorizedTotal,
      budget,
      actual,
      variance,
      x.projectCompletionDate || '',
      approvalSequence.length ? approvalSequence[approvalSequence.length - 1] : '',
      approvalSequence.join(' > '),
      approvalHistory.length,
      x.proposalsCount ?? (Array.isArray(x.proposals) ? x.proposals.length : 0),
      recommendedProposal?.vendorName || recommendedProposal?.vendor || '',
      recommendedAmount,
      Array.isArray(x.files) ? x.files.length : 0,
      Array.isArray(x.tags) ? x.tags.join('; ') : '',
      x.voidReason || '',
      x.amendmentReason || '',
      x.id ? `/dev/caf/${x.id}` : '',
    ].map(csvEscape)

    lines.push(line.join(','))
  }

  const blob = new Blob([lines.join('\n')], {
    type: 'text/csv;charset=utf-8;',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  const dt = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
  a.download = `caf-report-${dt}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/* ===== minimal scope helpers ===== */
function makeScope() {
  const sess = getSession() || {}
  const lvl = (sess.adminLevel || '').toLowerCase()
  const isAdmin = lvl === 'admin' || lvl === 'sysconfig'

  const allowed = new Set(
    (Array.isArray(sess.communities) ? sess.communities : [])
      .map(x => (x || '').toString().trim())
      .filter(Boolean),
  )

  return { isAdmin, allowed }
}

function norm(s) {
  return (s || '').toString().toLowerCase()
}

function joinTags(tags) {
  if (!Array.isArray(tags)) return ''
  return tags
    .map(t => (t || '').toString().trim())
    .filter(Boolean)
    .join(' ')
}

function extractNotesAndTags(caf) {
  const parts = []

  if (Array.isArray(caf?.tags)) {
    parts.push(joinTags(caf.tags))
  }

  // CAF-level messages/notes
  if (Array.isArray(caf?.messages)) {
    for (const m of caf.messages) {
      if (!m) continue
      parts.push(m.note || m.notes || m.message || m.text || '')
    }
  }

  // Files + proposals notes/tags
  if (Array.isArray(caf?.files)) {
    for (const f of caf.files) {
      if (!f) continue
      parts.push(f.notes || f.note || '')
      parts.push(joinTags(f.tags || f.tagList || f.labels))
    }
  }

  if (Array.isArray(caf?.proposals)) {
    for (const p of caf.proposals) {
      if (!p) continue
      parts.push(p.notes || p.note || '')
      parts.push(joinTags(p.tags || p.tagList || p.labels))
    }
  }

  return parts.filter(Boolean).join(' ')
}

export default function CafListDev() {
  const nav = useNavigate()
  const [rows, setRows] = React.useState([])
  const [loading, setLoading] = React.useState(true)

  // filters
  const [status, setStatus] = React.useState('All')
  const [stage, setStage] = React.useState('All')
  const [communityCode, setCommunityCode] = React.useState('All')
  const [q, setQ] = React.useState('')

  const [didInitCommunityFilter, setDidInitCommunityFilter] =
    React.useState(false)

  // community mapping
  const [commMap, setCommMap] = React.useState(() => new Map())
  const [commOptions, setCommOptions] = React.useState([
    { code: 'All', communityName: 'All Communities' },
  ])

  const { isAdmin, allowed } = React.useMemo(() => makeScope(), [])

  React.useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        setLoading(true)
        const [all, comms] = await Promise.all([
          listCafs(),
          listCommunitiesFS(),
        ])
        if (!alive) return

        const map = new Map(
          (comms || []).map(c => [
            c.code || c.name || c.id,
            c.communityName || c.name || c.code || c.id,
          ]),
        )
        setCommMap(map)

        // Build + sort communities by display name
        const allCommOpts = (comms || []).map(c => ({
          code: c.code || c.name || c.id,
          communityName: c.communityName || c.name || c.code || c.id,
        }))

        const sortedAllCommOpts = [...allCommOpts].sort((a, b) =>
          a.communityName.localeCompare(b.communityName),
        )

        const scopedCommOpts = isAdmin
          ? sortedAllCommOpts
          : sortedAllCommOpts.filter(
              c => allowed.has(c.code) || allowed.has(c.communityName),
            )

        // Keep "All Communities" pinned at the top
        setCommOptions([
          { code: 'All', communityName: 'All Communities' },
          ...scopedCommOpts,
        ])

        const scopedRows = isAdmin
          ? all || []
          : (all || []).filter(x => {
              const statusLower = String(x.status || '').toLowerCase()

              // Everyone can see completed CAFs, regardless of community
              if (statusLower === 'complete') return true

              const cc = x.communityCode || ''
              const cn = map.get(cc) || cc
              return allowed.has(cc) || allowed.has(cn)
            })

        setRows(scopedRows)

        // Default filter: non-admins land on their first community (alphabetical), not All
        if (
          !isAdmin &&
          !didInitCommunityFilter &&
          scopedCommOpts.length > 0
        ) {
          const first = scopedCommOpts[0]
          if (first && first.code) {
            setCommunityCode(first.code)
            setDidInitCommunityFilter(true)
          }
        }
      } finally {
        if (alive) setLoading(false)
      }
    })()

    return () => {
      alive = false
    }
  }, [isAdmin, allowed, didInitCommunityFilter])

  function getCommunityName(code) {
    if (!code) return '—'
    return commMap.get(code) || code
  }

  const filtered = React.useMemo(() => {
    const sQ = q.trim().toLowerCase()
    return rows.filter(x => {
      const st = (x.status || '').toLowerCase()
      const stg = x.stage || '—'
      const cc = x.communityCode || ''
      const communityName = getCommunityName(cc).toLowerCase()
      const notesAndTags = norm(extractNotesAndTags(x))

      const baseMatch =
        norm(x.number).includes(sQ) ||
        norm(x.title).includes(sQ) ||
        communityName.includes(sQ) ||
        norm(x.facilityType).includes(sQ) ||
        st.includes(sQ) ||
        stg.toLowerCase().includes(sQ) ||
        notesAndTags.includes(sQ)

      const statusOk =
        status === 'All'
          ? st !== 'void'
          : st === status
      const stageOk = stage === 'All' || stg === stage
      const commOk = communityCode === 'All' || cc === communityCode

      return (!sQ || baseMatch) && statusOk && stageOk && commOk
    })
  }, [rows, q, status, stage, communityCode, commMap])

  const sortByCreated = (a, b) => {
    const av = a.createdAt?.seconds
      ? a.createdAt.seconds * 1000
      : Date.parse(a.createdAt || 0) || 0

    const bv = b.createdAt?.seconds
      ? b.createdAt.seconds * 1000
      : Date.parse(b.createdAt || 0) || 0

    return bv - av
  }

  // ---- variance formatter (Actual - Budget) – UI only, not stored ----
  function renderVarianceCell(caf) {
    const rawBudget = caf.projectBudget
    const rawActual = caf.projectActual

    const b = Number(rawBudget)
    const a = Number(rawActual)

    const hasBudget =
      rawBudget !== null &&
      rawBudget !== undefined &&
      !Number.isNaN(b)
    const hasActual =
      rawActual !== null &&
      rawActual !== undefined &&
      !Number.isNaN(a)

    if (!hasBudget || !hasActual) {
      return <span className="muted">—</span>
    }

    const diff = a - b
    const abs = Math.abs(diff)
    const sign = diff > 0 ? '+' : diff < 0 ? '-' : ''
    const label = diff === 0 ? '$0' : `${sign}$${abs.toLocaleString()}`

    const color =
      diff > 0
        ? '#c62828' // over budget
        : diff < 0
        ? '#2e7d32' // under budget
        : '#6b7280' // exactly on

    return (
      <span style={{ color, fontWeight: 600 }}>
        {label}
      </span>
    )
  }

  return (
    <div>
      <h2
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 14,
          marginBottom: 12,
        }}
      >
        <span>Capital Approval Forms</span>
        <span style={{ flex: 1 }} />
        {isAdmin && (
          <button
            type="button"
            className="secondary"
            onClick={() => exportCafListCsv([...rows].sort(sortByCreated), getCommunityName)}
            disabled={loading || rows.length === 0}
            data-tooltip="Export all visible CAF records with current status and workflow details"
          >
            Export CAF Report
          </button>
        )}
        <button
          onClick={() => nav('/dev/caf/new')}
          style={{
            background: 'var(--color-primary)',
            color: '#fff',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            padding: '8px 14px',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: 'var(--shadow-1)',
          }}
        >
          + New CAF
        </button>
      </h2>

      {/* FILTER BAR */}
      <div
        className="mobile-stack-grid card"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 2fr 2fr auto',
          gap: 8,
        }}
      >
        <div>
          <label>Status</label>
          <select
            value={status}
            onChange={e => setStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>
                {s === 'All' ? 'All Statuses' : s}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Stage</label>
          <select
            value={stage}
            onChange={e => setStage(e.target.value)}
          >
            {STAGE_OPTIONS.map(s => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Community</label>
          <select
            value={communityCode}
            onChange={e => setCommunityCode(e.target.value)}
          >
            {commOptions.map(c => (
              <option key={c.code} value={c.code}>
                {c.communityName}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Search</label>
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search by number, title, community, status, stage, or facility type"
          />
        </div>

        <div>
          <button
            onClick={() => {
              setStatus('All')
              setStage('All')
              setCommunityCode('All')
              setQ('')
            }}
            style={{
              alignSelf: 'center',
              marginTop: 20,
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="card" style={{ marginTop: 12 }}>
        <div className="responsive-table-scroll" tabIndex={0} role="region" aria-label="Scrollable table"><table className="table caf-table">
          <colgroup>
            <col style={{ width: '16%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '19%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '7%' }} />
          </colgroup>
          <thead>
            <tr>
              <th className="nowrap">CAF #</th>
              <th className="nowrap">Community</th>
              <th>Title</th>
              <th className="nowrap" style={{ textAlign: 'center' }}>
                Status
              </th>
              <th>Stage</th>
              <th>Facility</th>
              <th className="nowrap" style={{ textAlign: 'right' }}>
                Contract
              </th>
              <th className="nowrap" style={{ textAlign: 'right' }}>
                Variance
              </th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8}>
                  <div className="muted">Loading…</div>
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <div className="muted">
                    No CAFs match your filter.
                  </div>
                </td>
              </tr>
            ) : (
              filtered.sort(sortByCreated).map(x => (
                <tr key={x.id}>
                  <td className="nowrap">
                    <Link
                      to={`/dev/caf/${encodeURIComponent(
                        x.id,
                      )}`}
                    >
                      {x.number || '—'}
                    </Link>
                  </td>

                  <td className="nowrap">{getCommunityName(x.communityCode)}</td>
                  <td className="wrap">
                    <div>{x.title || '—'}</div>
                    {x.isAmendment === true && (
                      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                        Amendment {x.amendmentNumber || '—'} to {x.parentCafNumber || x.rootCafNumber || 'original CAF'}
                      </div>
                    )}
                  </td>

                  {/* CENTERED STATUS PILL */}
                  <td className="status nowrap" style={{ textAlign: 'center' }}>
                    <span
                      className="badge"
                      style={{
                        padding: '3px 10px',
                        display: 'inline-block',
                      }}
                    >
                      {x.status || '—'}
                    </span>
                  </td>

                  <td className="wrap">{x.stage || '—'}</td>
                  <td className="wrap">{x.facilityType || '—'}</td>

                  <td className="nowrap" style={{ textAlign: 'right' }}>
                    {x.contractPrice != null
                      ? `$${Number(
                          x.contractPrice,
                        ).toLocaleString()}`
                      : '—'}
                  </td>

                  <td className="nowrap" style={{ textAlign: 'right' }}>
                    {renderVarianceCell(x)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table></div>
      </div>
    </div>
  )
}
